/**
 * Renders a single page of invalid/ambiguous AnalyzedRow results as a table,
 * reusing RowTable's presentation via the shared mapAnalyzedRowToRowData helper.
 */

import { useMemo } from 'react';

import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { AnalyzedRow } from '@/lib/csv-import/api';

import { RowTable } from './RowTable';
import type { RowCategory } from './types';
import { mapAnalyzedRowToRowData } from './types';

interface ProblemRowsTableProps {
  rows: AnalyzedRow[];
  category: 'invalid' | 'ambiguous';
  schemaMap: PropertySchemaMap;
  newPropertyLabels: Record<string, string>;
  label: string;
  /** Org id, forwarded to RowTable. Unused by these categories' cells, but the
   *  table takes it uniformly rather than making it optional. */
  orgId: number;
}

/**
 * `category` is narrowed to 'invalid' | 'ambiguous' (both assignable to the
 * broader RowCategory RowTable accepts) since those are the only categories
 * a problem-rows page can represent.
 */
export function ProblemRowsTable({
  rows,
  category,
  schemaMap,
  newPropertyLabels,
  label,
  orgId,
}: ProblemRowsTableProps) {
  // Keyed on the page-array index, NOT row.row_index — a /rows page can mix
  // files, and row_index is only unique per file, so reusing it would
  // collide and break react-aria Table row keys.
  const mapped = useMemo(
    () =>
      rows.map((row, i) =>
        mapAnalyzedRowToRowData(
          row,
          String(i),
          '',
          schemaMap,
          newPropertyLabels
        )
      ),
    [rows, schemaMap, newPropertyLabels]
  );

  if (mapped.length === 0) {
    return null;
  }

  return (
    <RowTable
      rows={mapped}
      label={label}
      schemaMap={schemaMap}
      newPropertyLabels={newPropertyLabels}
      category={category as RowCategory}
      entityTypeName={undefined}
      orgId={orgId}
    />
  );
}
